/** Экран результата: P₀/P₁/Δ, состав, прогнозы, ошибки, выплаты, комиссия по кругам, проверка обязательства. */
import { useEffect, useState } from 'react';
import type { GameView, SettlementView } from '@mt/protocol';
import { cr, pts } from '../lib/format';
import { verifyCommitment } from '../lib/verify';
import { downloadPackage, fetchPackage } from '../lib/verification';
import { client } from '../net/client';
import type { SignedPackage } from '@mt/verification';
import { VerifyDialog } from './VerifyDialog';

export function Settlement({ game, s }: { game: GameView; s: SettlementView }) {
  const [verified, setVerified] = useState<boolean | null>(null);
  const [showProof, setShowProof] = useState(false);
  useEffect(() => {
    if (s.commitment) void verifyCommitment(game.commitment.hash, s.commitment.salt, s.commitment.payload).then(setVerified);
  }, [s.commitment, game.commitment.hash]);
  const me = s.players.find((p) => game.seats.find((x) => x.isMe)?.playerId === p.playerId);
  const names = new Map(s.players.map((p) => [p.playerId, p.name]));
  const winners = s.players.filter((p) => p.payout > 0);
  return (
    <section className="settlement" aria-label="Результат розыгрыша">
      <header className={`result-head ${s.status}`}>
        <div>
          <h2>{s.status === 'cancelled' ? 'Розыгрыш отменён' : s.kind === 'walkover' ? 'Остался один участник' : 'Расчёт'}</h2>
          {s.reason && <p className="dim">{s.reason}{s.status === 'cancelled' ? ' · взносы возвращены, комиссия не удержана' : ''}</p>}
        </div>
        <dl className="stat-grid">
          <div>
            <dt>P₀</dt>
            <dd className="mono">{pts(s.p0)}</dd>
          </div>
          <div>
            <dt>P₁</dt>
            <dd className="mono">{pts(s.p1)}</dd>
          </div>
          <div>
            <dt>Δ</dt>
            <dd className={`mono ${s.delta && s.delta > 0 ? 'up' : s.delta && s.delta < 0 ? 'down' : ''}`}>{pts(s.delta, true)}</dd>
          </div>
          {me && (
            <div>
              <dt>Ваш итог</dt>
              <dd className={`mono ${me.net > 0 ? 'up' : me.net < 0 ? 'down' : ''}`}>{me.net > 0 ? '+' : ''}{cr(me.net)}</dd>
            </div>
          )}
        </dl>
      </header>

      <VerificationBand game={game} s={s} verified={verified} showProof={showProof} setShowProof={setShowProof} />

      <div className="composition">
        Состав агрегата:{' '}
        {s.composition.map((c) => (
          <span key={c.symbol} className="ticker">
            <b>{c.symbol}</b> {c.name}
          </span>
        ))}
      </div>

      <table className="grid-table">
        <thead>
          <tr>
            <th>Игрок</th>
            <th>Прогноз</th>
            <th className="num">Ошибка</th>
            <th className="num">Внесено</th>
            <th className="num">Выплата</th>
            <th className="num">Возврат</th>
            <th className="num">Итог</th>
          </tr>
        </thead>
        <tbody>
          {s.players.map((p) => (
            <tr key={p.playerId} className={`${p.payout > 0 ? 'win' : ''} ${p.playerId === me?.playerId ? 'me' : ''}`}>
              <td>
                {p.name}
                {p.folded && <em className="tag">пас</em>}
              </td>
              <td className={p.direction === 'up' ? 'up' : p.direction === 'down' ? 'down' : ''}>
                {p.direction === 'up' ? '▲' : p.direction === 'down' ? '▼' : '—'} {pts(p.points)}
                {s.kind === 'market' && !p.folded && !p.correctDirection && <em className="tag">неверное направление</em>}
              </td>
              <td className="num mono">{p.error === null ? '—' : pts(p.error)}</td>
              <td className="num mono">{cr(p.contributed)}</td>
              <td className="num mono">{p.payout ? cr(p.payout) : '—'}</td>
              <td className="num mono">{p.refund ? cr(p.refund) : '—'}</td>
              <td className={`num mono ${p.net > 0 ? 'up' : p.net < 0 ? 'down' : ''}`}>{p.net > 0 ? '+' : ''}{cr(p.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="settle-cols">
        <div>
          <h3>Банки</h3>
          <table className="grid-table compact">
            <thead>
              <tr>
                <th>Банк</th>
                <th className="num">Сумма</th>
                <th className="num">Комиссия</th>
                <th>Получатели (вес)</th>
              </tr>
            </thead>
            <tbody>
              {s.pots.map((p) => (
                <tr key={p.index}>
                  <td>{p.index === 0 ? 'Основной' : `Побочный ${p.index}`}</td>
                  <td className="num mono">{cr(p.amount)}</td>
                  <td className="num mono">{p.refunded ? '—' : cr(p.commission)}</td>
                  <td>
                    {p.refunded ? (
                      <span className="dim">возвращён внёсшим</span>
                    ) : (
                      <ul className="share-list">
                        {p.shares.map((sh) => (
                          <li key={sh.playerId}>
                            <span>{names.get(sh.playerId)}</span>
                            <span className="mono">{cr(sh.amount)}</span>
                            {sh.weight !== '—' && <span className="mono dim">{sh.weight.replace('.', ',')}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h3>Комиссия по кругам</h3>
          <table className="grid-table compact">
            <thead>
              <tr>
                <th>Круг</th>
                <th className="num">Новые взносы</th>
                <th className="num">Комиссия</th>
              </tr>
            </thead>
            <tbody>
              {s.commissionByRound.map((r) => (
                <tr key={r.round}>
                  <td>
                    {r.round}
                    {r.round === 1 && <span className="dim"> · с взносами</span>}
                  </td>
                  <td className="num mono">{cr(r.newContributions)}</td>
                  <td className="num mono">
                    {cr(r.commission)}
                    {r.cap !== null && r.commission === r.cap && r.commission > 0 && <em className="tag">потолок</em>}
                  </td>
                </tr>
              ))}
              <tr className="total">
                <td colSpan={2}>Удержано на счёт банка</td>
                <td className="num mono">{cr(s.bankDelta)}</td>
              </tr>
            </tbody>
          </table>
          <p className={`small ${s.totals.balanced ? 'ok' : 'down'}`}>
            Сверка: списано {cr(s.totals.debited)} = выплачено {cr(s.totals.paid)} + возвращено {cr(s.totals.refunded)} + банк {cr(s.totals.bank)} {s.totals.balanced ? '✓' : '✗'}
          </p>
        </div>
      </div>

      {winners.length === 0 && s.status === 'completed' && <p className="dim">Выигравших нет.</p>}
    </section>
  );
}

/** Полоса проверяемости: обязательство, пакет проверки, проверка в браузере. */
function VerificationBand(props: { game: GameView; s: SettlementView; verified: boolean | null; showProof: boolean; setShowProof: (f: (v: boolean) => boolean) => void }) {
  const { game, s, verified } = props;
  const [dialog, setDialog] = useState<SignedPackage | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<SignedPackage>) => {
    setBusy(true);
    try {
      setDialog(await fn());
    } catch (e) {
      client.toast('error', `Пакет проверки: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="verify-band">
      <div className="verify-band-main">
        <span className="verify-band-title">Проверяемость</span>
        <span className="muted-line">
          Обязательство <code>{game.commitment.hash.slice(0, 16)}…</code> опубликовано до ставок —{' '}
          {verified === true ? <b className="ok">совпадает ✓</b> : verified === false ? <b className="down">не совпадает ✗</b> : s.commitment ? 'проверяется…' : 'не раскрыто'}
        </span>
      </div>
      <div className="btn-row">
        {s.commitment && (
          <button className="btn tiny ghost" onClick={() => props.setShowProof((v) => !v)}>
            {props.showProof ? 'Скрыть соль' : 'Параметры и соль'}
          </button>
        )}
        {game.verificationAvailable && (
          <>
            <button className="btn tiny" disabled={busy} onClick={() => void run(() => fetchPackage(game.gameId).then((r) => r.signed))}>
              Проверить в браузере
            </button>
            <button className="btn tiny primary" disabled={busy} onClick={() => void run(() => downloadPackage(game.gameId))}>
              Скачать пакет проверки
            </button>
          </>
        )}
      </div>
      {props.showProof && s.commitment && (
        <pre className="proof-body">{`hash = SHA-256(salt + ":" + payload)
salt = ${s.commitment.salt}
payload = ${JSON.stringify(JSON.parse(s.commitment.payload), null, 2)}`}</pre>
      )}
      {dialog !== undefined && <VerifyDialog initial={dialog} onClose={() => setDialog(undefined)} />}
    </div>
  );
}
